/**
 * Exam-block mechanics (PLAN-Z M6, PLAN.md §3): one subtest, 20 items derived
 * from one block seed, a single 25:00 countdown, free navigation and
 * revisable answers, auto-submit at zero. Same block seed → identical 20
 * items, so blocks are retakeable and comparable. The adaptive selector is
 * deliberately NOT used here — a block mirrors the real test's fixed
 * composition (1–6 low, 7–14 medium, 15–20 high, following the official
 * ladder proportions).
 *
 * DOM-free and dependency-free so it is directly unit-testable.
 */
import { mulberry32 } from '../generators/prng.ts'
import { generateEquations, type EquationsPrompt } from '../generators/equations.ts'
import { generateFigures, type FiguresAnswer } from '../generators/figures.ts'
import { generateLatin, type LatinAnswer as LatinAnswerData } from '../generators/latin.ts'
import type { Difficulty, Question, Subtest } from '../generators/types.ts'
import { gradeEquations } from '../render/grade.ts'

export const BLOCK_ITEMS = 20
export const BLOCK_MINUTES = 25
export const SECONDS_PER_ITEM = 75

/** Fixed composition: items 1–6 low, 7–14 medium, 15–20 high. */
export function ladderDifficulty(index: number): Difficulty {
  if (index < 6) return 'low'
  if (index < 14) return 'medium'
  return 'high'
}

export interface BlockItem {
  index: number
  seed: number
  difficulty: Difficulty
  question: Question
}

export function buildBlock(subtest: Subtest, blockSeed: number): BlockItem[] {
  const draw = mulberry32(blockSeed)
  return Array.from({ length: BLOCK_ITEMS }, (_, index) => {
    const seed = Math.floor(draw() * 1e9)
    const difficulty = ladderDifficulty(index)
    const question =
      subtest === 'equations'
        ? generateEquations(difficulty, seed)
        : subtest === 'figures'
          ? generateFigures(difficulty, seed)
          : generateLatin(difficulty, seed)
    return { index, seed, difficulty, question }
  })
}

export type ItemAnswer = Record<string, string> | string | { blank1: number | null; blank2: number | null } | null

export interface GradedResponse {
  index: number
  correct: boolean
  yourAnswer: string
  rightAnswer: string
  subAnswers: Record<string, unknown>
}

/** Grade one block item into its scored RESPONSES: one for the text subtests,
 * two for figure items (blank 1 and blank 2 are scored independently,
 * EXAM.md §3). Unanswered responses grade as incorrect — the real exam has
 * no penalty, but blank ≠ correct. Per-letter / per-blank detail is recorded
 * either way. */
export function gradeBlockItem(item: BlockItem, answer: ItemAnswer): GradedResponse[] {
  const { index } = item
  if (item.question.subtest === 'equations') {
    const prompt = item.question.prompt as EquationsPrompt
    const values = (item.question.answer as { values: Record<string, number> }).values
    const submitted: Record<string, string> =
      answer !== null && typeof answer === 'object' && !('blank1' in answer)
        ? (answer as Record<string, string>)
        : {}
    const grade = gradeEquations(prompt, values, submitted)
    const fmt = (record: Record<string, number | string>) =>
      prompt.letters.map((L) => `${L}=${record[L] ?? ''}`).join(', ')
    const answered = prompt.letters.some((L) => (submitted[L] ?? '') !== '')
    return [
      {
        index,
        correct: grade.allCorrect,
        yourAnswer: answered ? fmt(submitted) : '—',
        rightAnswer: fmt(values),
        subAnswers: { perLetter: grade.perLetter, values: submitted },
      },
    ]
  }
  if (item.question.subtest === 'figures') {
    const key = item.question.answer as FiguresAnswer
    const picks =
      answer !== null && typeof answer === 'object' && !Array.isArray(answer)
        ? (answer as { blank1: number | null; blank2: number | null })
        : { blank1: null, blank2: null }
    const blank = (n: 1 | 2) => {
      const pick = n === 1 ? picks.blank1 : picks.blank2
      const right = key.correct[n - 1]
      return {
        index,
        correct: pick === right,
        yourAnswer: pick === null ? '—' : `option ${pick + 1}`,
        rightAnswer: `option ${right + 1}`,
        subAnswers: { blank: n, pick },
      }
    }
    return [blank(1), blank(2)]
  }
  const letter = (item.question.answer as LatinAnswerData).letter
  const pick = typeof answer === 'string' ? answer : null
  return [
    {
      index,
      correct: pick === letter,
      yourAnswer: pick ?? '—',
      rightAnswer: letter,
      subAnswers: { letter: pick },
    },
  ]
}

/** Pace (PLAN.md §3): while on item n (1-based), the budget is n × 75 s.
 * Positive result = ahead of pace (PLAN.md's "item 8 of 20 · 2:10 ahead"). */
export function paceAheadMs(elapsedMs: number, itemIndex0: number): number {
  return (itemIndex0 + 1) * SECONDS_PER_ITEM * 1000 - elapsedMs
}
